import { WritingInput, WritingTextarea } from "../../writing/writing-field";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { getOrCreateAuthSession } from "../../api/auth-client";
import type { AuthSessionResponse } from "../../api/auth.types";
import { clientEnv } from "../../env";
import { createSupport, getSupport, listSupport, updateSupport, SupportClientError,
  type SupportCategory, type SupportStatus, type SupportTicket, type SupportList,
  type CreateSupportInput, type UpdateSupportInput } from "../../api/support-client";
import type { OmnisyniaClientModule } from "../module.types";
import "./support.css";

const categories: Record<SupportCategory, string> = {
  chat: "Chat e inteligencia", voice: "Voz", images: "Imágenes", video: "Vídeo",
  billing: "Pagos y suscripción", account: "Cuenta y acceso", other: "Otro problema"
};
const statuses: Record<SupportStatus, string> = {
  open: "Abierta", in_progress: "En revisión", waiting_user: "Esperando tu respuesta", resolved: "Resuelta"
};
function date(value: string) { return new Date(value).toLocaleString("es-ES"); }
function platform(): string {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "iOS" :
    /Android/i.test(navigator.userAgent) ? "Android" : "Web";
}

function Support() {
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [list, setList] = useState<SupportList | null>(null);
  const [all, setAll] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<SupportCategory>("other");
  const [diagnostics, setDiagnostics] = useState(false);
  const [reply, setReply] = useState("");
  const [nextStatus, setNextStatus] = useState<SupportStatus>("open");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const alive = useRef(false);
  const lock = useRef(false);
  const pendingCreate = useRef<CreateSupportInput | null>(null);
  const pendingUpdate = useRef<{ ticketId: string; body: UpdateSupportInput } | null>(null);

  useEffect(() => {
    alive.current = true;
    void (async () => {
      try {
        const account = await getOrCreateAuthSession();
        const result = await listSupport(account.sessionToken);
        if (alive.current) { setSession(account); setList(result); }
      } catch (e) { if (alive.current) setError(e instanceof Error ? e.message : "No se pudo abrir el canal."); }
      finally { if (alive.current) setBusy(false); }
    })();
    return () => { alive.current = false; };
  }, []);

  async function run(action: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(""); setNotice("");
    try { await action(); }
    catch (e) { if (alive.current) setError(e instanceof Error ? e.message : "No se pudo completar la operación."); }
    finally { lock.current = false; if (alive.current) setBusy(false); }
  }
  async function refresh(scope = all, targetPage = page) {
    if (!session) return;
    const result = await listSupport(session.sessionToken, scope, targetPage);
    if (alive.current) { setList(result); setAll(scope); setPage(targetPage); }
  }
  function show(ticket: SupportTicket) {
    setSelected(ticket);
    setNextStatus(ticket.status);
  }
  async function openTicket(id: string) {
    if (!session) return;
    if (selected?.id !== id && (reply.trim() || pendingUpdate.current) &&
      !window.confirm("Hay una respuesta sin enviar. ¿Descartarla para abrir otra incidencia?")) return;
    await run(async () => {
      const result = await getSupport(session.sessionToken, id);
      if (alive.current) { show(result.ticket); setReply(""); pendingUpdate.current = null; }
    });
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!session) return;
    await run(async () => {
      if (!pendingCreate.current) pendingCreate.current = {
        requestId: crypto.randomUUID(), title: title.trim(), description: description.trim(), category,
        ...(diagnostics ? { diagnostics: { appVersion: clientEnv.appVersion, platform: platform() } } : {})
      };
      try {
        const result = await createSupport(session.sessionToken, pendingCreate.current);
        pendingCreate.current = null;
        if (!alive.current) return;
        setTitle(""); setDescription(""); setDiagnostics(false);
        // Do not discard a reply already being written to another ticket.
        if (!reply.trim() && !pendingUpdate.current) show(result.ticket);
        setNotice(result.durable
          ? "Incidencia registrada. Consulta aquí su estado y las respuestas de soporte."
          : "Registro temporal: el servidor todavía no tiene activado el guardado permanente.");
        await refresh(all, 0);
      } catch (e) {
        if (e instanceof SupportClientError && e.status >= 400 && e.status < 500) pendingCreate.current = null;
        throw e;
      }
    });
  }
  async function sendReply(event: FormEvent) {
    event.preventDefault();
    if (!session || !selected) return;
    await run(async () => {
      if (!pendingUpdate.current) pendingUpdate.current = {
        ticketId: selected.id,
        body: { requestId: crypto.randomUUID(), expectedVersion: selected.version,
          message: reply.trim(), ...(nextStatus !== selected.status ? { status: nextStatus } : {}) }
      };
      try {
        const pending = pendingUpdate.current;
        const result = await updateSupport(session.sessionToken, pending.ticketId, pending.body);
        pendingUpdate.current = null;
        if (!alive.current) return;
        show(result.ticket); setReply("");
        setNotice("Respuesta y estado guardados.");
        await refresh();
      } catch (e) {
        if (e instanceof SupportClientError && e.status >= 400 && e.status < 500) {
          pendingUpdate.current = null;
          if (e.status === 409) {
            const latest = await getSupport(session.sessionToken, selected.id);
            if (alive.current) show(latest.ticket);
          }
        }
        throw e;
      }
    });
  }
  const staff = list?.staff === true;
  const allowedStatuses: SupportStatus[] = staff ? ["open", "in_progress", "waiting_user", "resolved"] : ["open", "resolved"];
  const frozenCreate = busy || pendingCreate.current !== null;
  const frozenReply = busy || pendingUpdate.current !== null;
  return <section className="support-shell" aria-labelledby="support-title">
    <header><p className="eyebrow">OMNISYNIA</p><h2 id="support-title">Ayuda e incidencias</h2>
      <p>Cuéntanos qué falla. Podrás seguir tu incidencia y conversar con soporte desde aquí.</p></header>
    {error && <p className="support-error" role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!session && !busy && <button className="secondary-button" onClick={() => window.location.reload()}>Volver a intentar</button>}
    {list && !list.durable && <p role="status">Modo de prueba: las incidencias no se conservarán si se reinicia el servidor.</p>}
    {session?.user.mode === "anonymous" && <p>Estás como invitado. Entra en tu cuenta antes de enviar para poder recuperar la incidencia desde otro dispositivo.</p>}
    <details className="support-new" open={!selected}>
      <summary>Comunicar un problema</summary>
      <form onSubmit={submit}>
        <label>Tipo de problema<select value={category} disabled={frozenCreate} onChange={e => setCategory(e.target.value as SupportCategory)}>
          {Object.entries(categories).map(([key,label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <WritingInput label="Título" value={title} disabled={frozenCreate} minLength={3} maxLength={120} required onValueChange={text => setTitle(text)} />
        <WritingTextarea label="Qué ha ocurrido" value={description} disabled={frozenCreate} minLength={10} maxLength={4000} rows={5} required
          placeholder="Qué estabas intentando hacer, qué ocurrió y qué esperabas."
          onValueChange={text => setDescription(text)} />
        <p className="support-help">Incluye solo la información necesaria. No envíes contraseñas, claves API ni números completos de tarjeta.</p>
        <label className="support-check"><input type="checkbox" checked={diagnostics} disabled={frozenCreate}
          onChange={e => setDiagnostics(e.target.checked)} />Adjuntar versión de la app y tipo de dispositivo.</label>
        <p className="support-help">El mensaje será visible para ti y para el personal autorizado de soporte. Las respuestas aparecerán aquí.</p>
        <button className="primary-button" type="submit" disabled={busy || !session || title.trim().length < 3 || description.trim().length < 10}>
          {pendingCreate.current ? "Reintentar envío" : "Enviar incidencia"}
        </button>
      </form>
    </details>
    <div className="support-toolbar">
      <h3>{all ? "Bandeja de soporte" : "Mis incidencias"}</h3>
      {staff && <button className="secondary-button" disabled={busy} onClick={() => void run(() => refresh(!all,0))}>
        {all ? "Ver mis incidencias" : "Gestionar todas"}
      </button>}
      <button className="secondary-button" disabled={busy || !session} onClick={() => void run(async () => {
        await refresh();
        if (selected && session) {
          const result = await getSupport(session.sessionToken, selected.id);
          if (alive.current) show(result.ticket);
        }
      })}>Actualizar</button>
    </div>
    <div className="support-layout">
      <div>
        {list?.tickets.length === 0 && <p>No hay incidencias en esta página.</p>}
        <ul className="support-list" aria-label="Lista de incidencias">
          {list?.tickets.map(ticket => <li key={ticket.id}>
            <button type="button" disabled={busy} className={selected?.id === ticket.id ? "selected" : ""} onClick={() => void openTicket(ticket.id)}>
              <strong>{ticket.title}</strong><span>{categories[ticket.category]} · {statuses[ticket.status]}</span>
              <small>{date(ticket.updatedAt)}</small>
            </button>
          </li>)}
        </ul>
        <div className="support-toolbar">
          <button className="secondary-button" disabled={busy || page === 0} onClick={() => void run(() => refresh(all,page-1))}>Anterior</button>
          <span>Página {page + 1}</span>
          <button className="secondary-button" disabled={busy || !list?.hasMore} onClick={() => void run(() => refresh(all,page+1))}>Siguiente</button>
        </div>
      </div>
      {selected && <article className="support-ticket" aria-labelledby="ticket-title">
        <h3 id="ticket-title">{selected.title}</h3>
        <p>{statuses[selected.status]} · {categories[selected.category]}</p>
        <small className="support-reference">Referencia: {selected.id}</small>
        {selected.diagnostics && <p className="support-help">Versión {selected.diagnostics.appVersion} · {selected.diagnostics.platform}</p>}
        <ol className="support-thread">
          {selected.entries.map(entry => <li key={entry.id}>
            <strong>{entry.author === "support" ? "Soporte OMNISYNIA" : "Usuario"}</strong>
            <small>{date(entry.createdAt)}</small>
            {entry.text && <p>{entry.text}</p>}
            <span className="support-help">{statuses[entry.status]}</span>
          </li>)}
        </ol>
        <form onSubmit={sendReply}>
          <WritingTextarea label="Tu respuesta" key={selected.id} rows={4} maxLength={4000} value={reply} disabled={frozenReply} onValueChange={text => setReply(text)} />
          <label>Estado<select disabled={frozenReply} value={nextStatus} onChange={e => setNextStatus(e.target.value as SupportStatus)}>
            {!allowedStatuses.includes(selected.status) && <option value={selected.status}>{statuses[selected.status]}</option>}
            {allowedStatuses.map(status => <option key={status} value={status}>{statuses[status]}</option>)}
          </select></label>
          <button className="primary-button" disabled={busy || (!reply.trim() && nextStatus === selected.status && !pendingUpdate.current)} type="submit">
            {pendingUpdate.current ? "Reintentar respuesta" : reply.trim() ? "Enviar respuesta" : "Guardar estado"}
          </button>
        </form>
      </article>}
    </div>
  </section>;
}
export const supportClientModule: OmnisyniaClientModule = {
  id: "support", displayName: "Ayuda e incidencias",
  description: "Comunica problemas y consulta las respuestas de soporte.",
  tier: "core", status: "available", Component: Support
};
